import { z } from "zod";
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
/** UTC+14 is the earliest local day on Earth (Line Islands). */
const LATEST_UTC_OFFSET_MS = 14 * 60 * 60 * 1000;
function pad2(value) {
    return String(value).padStart(2, "0");
}
function formatYmd(year, month, day) {
    return `${String(year).padStart(4, "0")}-${pad2(month)}-${pad2(day)}`;
}
function daysInMonth(year, month) {
    if (month === 2) {
        const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        return leap ? 29 : 28;
    }
    return [4, 6, 9, 11].includes(month) ? 30 : 31;
}
export function parseDateOnlyParts(value) {
    const match = DATE_ONLY_PATTERN.exec(value);
    if (!match)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (year < 1 || month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) {
        return null;
    }
    return { year, month, day };
}
export function isValidDateOnly(value) {
    return parseDateOnlyParts(value) != null;
}
/** The local calendar date in the browser or process timezone. Client pickers only. */
export function localTodayDateOnly(now = new Date()) {
    return formatYmd(now.getFullYear(), now.getMonth() + 1, now.getDate());
}
/**
 * The latest calendar date that has already started somewhere on Earth.
 * Server-side "not in the future" checks use this, so a user in any timezone
 * can always enter their own today.
 */
export function latestStartedDateOnly(now = new Date()) {
    const ahead = new Date(now.getTime() + LATEST_UTC_OFFSET_MS);
    return formatYmd(ahead.getUTCFullYear(), ahead.getUTCMonth() + 1, ahead.getUTCDate());
}
export function dateOnlyIssue(value, options = {}) {
    if (!DATE_ONLY_PATTERN.test(value))
        return "format";
    if (!isValidDateOnly(value))
        return "invalid_date";
    if (options.maxDate != null && value > options.maxDate)
        return "future";
    return null;
}
export function birthdayIssue(value, now = new Date()) {
    return dateOnlyIssue(value, { maxDate: latestStartedDateOnly(now) });
}
/**
 * Database `DATE` columns are read and written as UTC midnight, so the stored
 * calendar date never depends on the process or browser timezone.
 */
export function dateOnlyToUtcDate(value) {
    const parts = parseDateOnlyParts(value);
    if (!parts) {
        throw new RangeError(`Invalid date-only value: ${value}`);
    }
    const date = new Date(0);
    date.setUTCFullYear(parts.year, parts.month - 1, parts.day);
    date.setUTCHours(0, 0, 0, 0);
    return date;
}
export function utcDateToDateOnly(date) {
    return formatYmd(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}
/** Locale display for a stored calendar date. Never shifts the day. */
export function formatDateOnlyForLocale(value, locale, options = { day: "numeric", month: "long", year: "numeric" }) {
    const date = dateOnlyToUtcDate(value);
    return new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" }).format(date);
}
export const birthdaySchema = z
    .string()
    .trim()
    .superRefine((value, ctx) => {
    const issue = birthdayIssue(value);
    if (issue) {
        ctx.addIssue({ code: "custom", message: `birthday:${issue}` });
    }
});
//# sourceMappingURL=date-only.js.map